import { useState } from "react";
import { useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import { FundoMarca } from "../misc/FundoMarca";
import { enviarContratacao, infoDoLink, soDigitos, type TipoContratacao } from "./dados";

/**
 * Página pública do link de contratação (migration 287): o corretor da
 * parceira preenche o cadastro; o pedido segue para liberação da North.
 * Sem login: a validação e as travas ficam em contratacao_enviar().
 */

const TIPOS: { codigo: TipoContratacao; titulo: string; texto: string }[] = [
  { codigo: "individual", titulo: "Individual", texto: "Um usuário, vinculado à parceira." },
  { codigo: "escritorio", titulo: "Escritório", texto: "Organização própria, com vários usuários." },
];

const Campo = ({
  id,
  rotulo,
  ...props
}: { id: string; rotulo: string } & React.ComponentProps<typeof Input>) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{rotulo}</Label>
    <Input id={id} {...props} />
  </div>
);

export const ContratarPage = () => {
  const { codigo = "" } = useParams();
  const { data: link, isLoading, error: erroLink } = useQuery({
    queryKey: ["contratacao-link", codigo],
    queryFn: () => infoDoLink(codigo),
    retry: false,
  });

  const [tipo, setTipo] = useState<TipoContratacao>("individual");
  const [nome, setNome] = useState("");
  const [documento, setDocumento] = useState("");
  const [email, setEmail] = useState("");
  const [celular, setCelular] = useState("");
  const [escritorioNome, setEscritorioNome] = useState("");
  const [escritorioCnpj, setEscritorioCnpj] = useState("");
  const [usuarios, setUsuarios] = useState("");
  const [aceite, setAceite] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviado, setEnviado] = useState(false);

  const escritorio = tipo === "escritorio";
  const completo =
    nome.trim() &&
    [11, 14].includes(soDigitos(documento).length) &&
    email.includes("@") &&
    soDigitos(celular).length >= 10 &&
    (!escritorio || escritorioNome.trim()) &&
    aceite;

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!completo || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await enviarContratacao({
        codigo,
        tipo,
        nome,
        cpfCnpj: documento,
        email,
        celular,
        escritorioNome,
        escritorioCnpj,
        usuariosPrevistos: usuarios ? Number(usuarios) : null,
      });
      setEnviado(true);
    } catch (err) {
      setErro((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <FundoMarca cor={link?.cor_primaria}>
      <div className="w-full max-w-lg rounded-2xl bg-card p-6 text-card-foreground shadow-xl sm:p-8">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : erroLink || !link ? (
          <div className="space-y-2 py-6 text-center">
            <h1 className="text-lg font-semibold">Link indisponível</h1>
            <p className="text-sm text-muted-foreground">Link de contratação inválido ou encerrado.</p>
          </div>
        ) : enviado ? (
          <div className="space-y-2 py-6 text-center">
            <h1 className="text-lg font-semibold">Cadastro recebido</h1>
            <p className="text-sm text-muted-foreground">
              Pedido em análise. O convite de acesso segue para {email.trim().toLowerCase()} após a liberação.
            </p>
          </div>
        ) : (
          <form className="space-y-5" onSubmit={enviar}>
            <div className="space-y-1">
              {link.logo_url && <img className="mb-3 h-10 max-w-[160px] object-contain" src={link.logo_url} alt={link.parceira} />}
              <h1 className="text-xl font-semibold">Contratação da plataforma North</h1>
              <p className="text-sm text-muted-foreground">Indicação {link.parceira}</p>
            </div>

            <fieldset className="space-y-2">
              <legend className="mb-1.5 text-sm font-medium">Tipo de contratação</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {TIPOS.map((t) => (
                  <button
                    key={t.codigo}
                    type="button"
                    aria-pressed={tipo === t.codigo}
                    onClick={() => setTipo(t.codigo)}
                    className={cn(
                      "rounded-lg border p-3 text-left transition-colors",
                      tipo === t.codigo ? "border-marinho bg-marinho/5 ring-1 ring-marinho" : "hover:bg-muted/50",
                    )}
                  >
                    <div className="text-sm font-semibold">{t.titulo}</div>
                    <div className="text-xs text-muted-foreground">{t.texto}</div>
                  </button>
                ))}
              </div>
            </fieldset>

            {escritorio && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Campo id="esc-nome" rotulo="Nome do escritório" value={escritorioNome} onChange={(e) => setEscritorioNome(e.target.value)} maxLength={120} />
                </div>
                <Campo id="esc-cnpj" rotulo="CNPJ do escritório (opcional)" inputMode="numeric" value={escritorioCnpj} onChange={(e) => setEscritorioCnpj(e.target.value)} />
                <Campo id="esc-usuarios" rotulo="Usuários previstos" type="number" min={1} max={500} value={usuarios} onChange={(e) => setUsuarios(e.target.value)} />
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Campo id="nome" rotulo={escritorio ? "Nome do responsável" : "Nome completo"} autoComplete="name" value={nome} onChange={(e) => setNome(e.target.value)} maxLength={120} />
              </div>
              <Campo id="documento" rotulo="CPF ou CNPJ" inputMode="numeric" value={documento} onChange={(e) => setDocumento(e.target.value)} />
              <Campo id="celular" rotulo="Celular" inputMode="tel" autoComplete="tel" placeholder="(41) 99999-9999" value={celular} onChange={(e) => setCelular(e.target.value)} />
              <div className="sm:col-span-2">
                <Campo id="email" rotulo="E-mail de acesso" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={160} />
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-sm">
              <Checkbox className="mt-0.5" checked={aceite} onCheckedChange={(v) => setAceite(v === true)} />
              <span className="text-muted-foreground">
                Declaração de veracidade dos dados e autorização de contato da North para a contratação, com tratamento dos dados conforme a LGPD.
              </span>
            </label>

            {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}

            <Button type="submit" className="w-full bg-marinho" disabled={!completo || enviando}>
              {enviando ? "Enviando..." : "Enviar cadastro"}
            </Button>
          </form>
        )}
      </div>
    </FundoMarca>
  );
};

ContratarPage.path = "/contratar/:codigo";
